import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ShieldCheckIcon,
  UsersIcon,
  MegaphoneIcon,
} from '@heroicons/react/24/outline';
import { usePermissions } from '../../hooks/usePermissions';

/**
 * Admin Dashboard — landing page for users with the `admin` role.
 *
 * Acts as a hub linking to the existing admin tools:
 *   • Role Management            → /admin/roles
 *   • User Role Associations     → /admin/user-roles
 *   • Permission Tester          → /admin/permission-tester
 *
 * Access is gated by the route guard (`PermissionBasedRoute`), so this
 * component assumes the caller already has at least `roles:read`. The
 * individual cards are still filtered by the relevant permissions so
 * non-admin roles that happen to hold a subset of admin perms only see
 * what they can act on.
 */
const AdminDashboard: React.FC = () => {
  const { t } = useTranslation(['admin', 'common']);
  const { hasPermission, hasRole } = usePermissions();

  const isAdmin = hasRole('admin');
  const canReadRoles = hasPermission('roles:read');
  const canAssociateRoles = hasPermission('roles:associate');

  const tiles = [
    {
      to: '/admin/users',
      show: canReadRoles,
      icon: UsersIcon,
      title: t('admin:sidebar.users', 'User Management'),
      description: t(
        'admin:dashboard.usersDescription',
        'Browse and manage all users on the platform.',
      ),
      iconClass: 'bg-blue-100 text-blue-600',
    },
    {
      to: '/admin/user-roles',
      show: canAssociateRoles,
      icon: ShieldCheckIcon,
      title: t('admin:sidebar.accountsAndRoles', 'Roles & Permissions'),
      description: t(
        'admin:dashboard.rolesDescription',
        'Create, edit, and delete roles and their permissions.',
      ),
      iconClass: 'bg-green-100 text-green-600',
    },
    {
      to: '/admin/campaigns',
      show: canReadRoles,
      icon: MegaphoneIcon,
      title: t('admin:sidebar.campaignList', 'Campaign List'),
      description: t(
        'admin:dashboard.campaignsDescription',
        'View and manage all campaigns grouped by status.',
      ),
      iconClass: 'bg-purple-100 text-purple-600',
    },
  ];

  const visibleTiles = tiles.filter((tile) => tile.show);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">
          {t('admin:dashboard.title', 'Admin Dashboard')}
        </h1>
        <p className="text-gray-600 mt-2">
          {isAdmin
            ? t(
                'admin:dashboard.subtitleAdmin',
                'Manage roles, permissions, and user access across the platform.',
              )
            : t(
                'admin:dashboard.subtitleLimited',
                'Administrative tools available for your role.',
              )}
        </p>
      </div>

      {visibleTiles.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
          <p className="text-gray-500">
            {t(
              'admin:dashboard.noTools',
              'No administrative tools are available for your role.',
            )}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {visibleTiles.map((tile) => {
            const Icon = tile.icon;
            return (
              <Link
                key={tile.to}
                to={tile.to}
                className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:border-blue-300 hover:shadow-md transition"
              >
                <div className="flex items-start">
                  <div className={`p-3 rounded-lg ${tile.iconClass}`}>
                    <Icon className="h-6 w-6" />
                  </div>
                  <div className="ml-4 flex-1">
                    <h3 className="text-base font-semibold text-gray-900">
                      {tile.title}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      {tile.description}
                    </p>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
